import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import NavBar from './NavBar';
import Footer from './Footer';

const SECTIONS = [
  {
    id: 'information-we-collect',
    title: 'Information We Collect',
    body: `We collect information you voluntarily provide when you contact us through our website forms — including your name, email address, and the content of your message. We do not collect payment information directly; any payment processing is handled by third-party providers with their own privacy policies.

We may also collect non-personal technical data through analytics tools, including page views, browser type, and general geographic location (country/city level). This data is aggregated and does not identify you personally.`
  },
  {
    id: 'how-we-use-your-information',
    title: 'How We Use Your Information',
    body: `We use the information you provide solely to respond to your enquiry and manage our professional relationship. We do not sell, rent, or share your personal data with third parties for marketing purposes.

We may use anonymised analytics data to improve the performance and content of our website.`
  },
  {
    id: 'cookies',
    title: 'Cookies',
    body: `Our website may use essential cookies required for basic site functionality. We may also use analytics cookies (such as those provided by Google Analytics or equivalent services) to understand how visitors use our site. You may disable cookies in your browser settings; however, some site features may not function correctly as a result.`
  },
  {
    id: 'data-retention',
    title: 'Data Retention',
    body: `We retain contact form submissions for as long as necessary to fulfil the purpose for which they were submitted, or as required by applicable law. You may request deletion of your data at any time by contacting us at hello@quanturama.com.`
  },
  {
    id: 'third-party-services',
    title: 'Third-Party Services',
    body: `We use third-party services including form processing tools (Formspree), analytics tools, and scheduling software. These services operate under their own privacy policies. We encourage you to review those policies independently.`
  },
  {
    id: 'your-rights',
    title: 'Your Rights',
    body: `If you are located in the European Economic Area (EEA), you have the right to access, correct, or delete your personal data, object to or restrict processing, and lodge a complaint with your local data protection authority.

To exercise any of these rights, contact us at hello@quanturama.com.`
  },
  {
    id: 'security',
    title: 'Security',
    body: `We take reasonable technical and organisational measures to protect your personal data against unauthorised access, alteration, disclosure, or destruction. However, no method of transmission over the internet is completely secure.`
  },
  {
    id: 'changes-to-this-policy',
    title: 'Changes to This Policy',
    body: `We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated effective date. Continued use of our website following any changes constitutes your acceptance of the revised policy.`
  },
  {
    id: 'contact',
    title: 'Contact',
    body: `For any questions or concerns regarding this Privacy Policy, please contact us at hello@quanturama.com.`
  }
];

const Privacy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Privacy Policy | Quanturama</title>
        <meta name="description" content="Quanturama Privacy Policy — how we collect, use, and protect your personal data." />
        <link rel="canonical" href="https://quanturama.com/privacy-policy" />
      </Helmet>

      <NavBar />

      <main style={{ paddingTop: '6rem', paddingBottom: '8rem', minHeight: '100vh' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto', padding: '0 1.5rem' }}>

          {/* Breadcrumb */}
          <Link
            to="/"
            style={{
              fontSize: '11px',
              letterSpacing: '0.25em',
              textTransform: 'uppercase',
              opacity: 0.35,
              textDecoration: 'none',
              color: 'inherit',
              display: 'inline-block',
              marginBottom: '2.5rem',
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={e => e.target.style.opacity = 0.7}
            onMouseLeave={e => e.target.style.opacity = 0.35}
          >
            ← Back to Quanturama
          </Link>

          {/* Header */}
          <div style={{
            borderBottom: '1px solid rgba(255,255,255,0.07)',
            paddingBottom: '2.5rem',
            marginBottom: '3rem'
          }}>
            <p style={{
              fontSize: '10px',
              letterSpacing: '0.4em',
              textTransform: 'uppercase',
              opacity: 0.3,
              marginBottom: '1rem'
            }}>
              Legal Document
            </p>
            <h1 style={{
              fontSize: 'clamp(2rem, 4vw, 2.8rem)',
              fontWeight: 700,
              lineHeight: 1.15,
              marginBottom: '1rem',
              letterSpacing: '-0.02em'
            }}>
              Privacy Policy
            </h1>
            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', opacity: 0.35 }}>Effective date: 1 January 2025</span>
              <span style={{ fontSize: '12px', opacity: 0.35 }}>Last updated: March 2026</span>
            </div>
          </div>

          {/* Intro */}
          <p style={{
            fontSize: '1rem',
            lineHeight: 1.85,
            opacity: 0.65,
            marginBottom: '3.5rem',
            borderLeft: '2px solid rgba(255,255,255,0.12)',
            paddingLeft: '1.25rem'
          }}>
            Quanturama ("we", "us", "our") is committed to protecting your personal data. This Privacy Policy
            explains what information we collect, how we use it, and your rights in relation to it.
          </p>

          {/* Table of Contents */}
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: '8px',
            padding: '1.5rem 2rem',
            marginBottom: '4rem'
          }}>
            <p style={{
              fontSize: '10px',
              letterSpacing: '0.35em',
              textTransform: 'uppercase',
              opacity: 0.35,
              marginBottom: '1rem'
            }}>
              Contents
            </p>
            <ol style={{ margin: 0, paddingLeft: '1.25rem' }}>
              {SECTIONS.map((s, i) => (
                <li key={s.id} style={{ marginBottom: '0.4rem' }}>
                  <a
                    href={`#${s.id}`}
                    style={{
                      fontSize: '0.85rem',
                      opacity: 0.5,
                      textDecoration: 'none',
                      color: 'inherit',
                      transition: 'opacity 0.2s'
                    }}
                    onMouseEnter={e => e.target.style.opacity = 1}
                    onMouseLeave={e => e.target.style.opacity = 0.5}
                  >
                    {i + 1}. {s.title}
                  </a>
                </li>
              ))}
            </ol>
          </div>

          {/* Sections */}
          {SECTIONS.map(({ id, title, body }, i) => (
            <div
              key={id}
              id={id}
              style={{
                marginBottom: '3rem',
                paddingBottom: '3rem',
                borderBottom: i < SECTIONS.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', marginBottom: '1rem' }}>
                <span style={{ fontSize: '10px', opacity: 0.25, fontVariantNumeric: 'tabular-nums', minWidth: '1.5rem' }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h2 style={{
                  fontSize: '0.78rem',
                  letterSpacing: '0.25em',
                  textTransform: 'uppercase',
                  opacity: 0.55,
                  fontWeight: 600
                }}>
                  {title}
                </h2>
              </div>
              {body.split('\n\n').map((para, j) => (
                <p key={j} style={{
                  fontSize: '0.92rem',
                  lineHeight: 1.9,
                  opacity: 0.65,
                  marginBottom: '0.9rem',
                  paddingLeft: '2.25rem'
                }}>
                  {para}
                </p>
              ))}
            </div>
          ))}

        </div>
      </main>

      <Footer />
    </>
  );
};

export default Privacy;
